import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu for communities and groups
 *
 * @prop communityId - The id of a community
 * @prop groupId - The id of a group if group is present
 * @prop unitName - Title of community or group
 * @prop subsection - Used for toggle notifications for particular unit section (options: stream, forum, marketplace, booking)
 * @prop buttonClass - For button style adjustment
 * @prop notificationsOn - Show notification action
 * @prop notificationsValue - true or false
 * @prop favoriteOn - Show favorite action
 * @prop favoriteValue - true or false
 * @prop tagsOn - Show tags action
 * @prop reportOn - Show report action
 * @prop editOn - Show edit action
 * @prop leaveOn - Show leave action
 * @prop adminOn - Show admin action
 * @prop unitId - Group id or Community id (internal state)
 */

export default class GroupMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  /** @internal */
  static styles = [BaseMenu.styles, style];

  static properties = {
    communityId: { type: Number },
    groupId: { type: Number },
    unitName: { type: String },
    subsection: { type: String },
    buttonClass: { type: String },
    notificationsOn: { type: Boolean },
    notificationsValue: { type: Boolean },
    favoriteOn: { type: Boolean },
    favoriteValue: { type: Boolean },
    tagsOn: { type: Boolean },
    reportOn: { type: Boolean },
    editOn: { type: Boolean },
    leaveOn: { type: Boolean },
    adminOn: { type: Boolean },
    unitId: { state: true },
  };

  /** @internal */
  get instanceId() {
    return this.unitId;
  }

  /** @internal */
  get unitId() {
    return this.groupId || this.communityId;
  }

  /** @internal */
  get communityOrGroupUrl() {
    if (this.groupId) {
      return `/communities/${this.communityId}/groups/${this.groupId}`;
    }
    return `/communities/${this.communityId}`;
  }

  /** @internal */
  get toggleNotificationsUrl() {
    return `/units/notifications/${this.unitId}/toggle?section=${this.subsection}`;
  }

  /** @internal */
  get toggleFavoriteUrl() {
    return `/units/favorites/${this.unitId}/toggle`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.unitId}&complaint[complainable_type]=Group`;
  }

  /** @internal */
  get tagsUrl() {
    return `${this.communityOrGroupUrl}/file_browser/tags`;
  }

  /** @internal */
  get editUrl() {
    return this.isSmallDevice
      ? `${this.communityOrGroupUrl}/settings`
      : `${this.communityOrGroupUrl}/settings/basic`;
  }

  /** @internal */
  get leaveUrl() {
    return `/units/${this.unitId}/memberships/leave`;
  }

  /** @internal */
  get adminUrl() {
    return `/admin/communities/${this.unitId}`;
  }

  /** @internal */
  get isSmallDevice() {
    return window.innerWidth < 768;
  }

  /** @internal */
  get notificationItem() {
    return {
      title: this.textBaseOnActiveKey(
        'actions.notifications',
        'notificationsValue',
        { name: this.t(`subsections.${this.subsection}`) }
      ),
      action: this.actionFor('toggleNotification'),
      icon: this.notificationsValue ? 'bell-s' : 'bell',
      partName: `notifications ${this.notificationsValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get favoriteItem() {
    return {
      title: this.textBaseOnActiveKey('actions.favorite', 'favoriteValue'),
      action: this.actionFor('toggleFavorite'),
      icon: this.favoriteValue ? 'star-s' : 'star',
      partName: `favorite ${this.favoriteValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get tagsItem() {
    return {
      title: this.t('actions.tags'),
      action: this.actionFor('groupVisit', [this.tagsUrl]),
      icon: 'folder',
      partName: 'tags',
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.inappropriate_report'),
      action: this.actionFor('groupExecJSFromResponse', [this.reportUrl]),
      icon: 'exclamation-triangle',
      partName: 'report',
    };
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t('actions.settings'),
      action: this.actionFor('groupVisit', [this.editUrl]),
      icon: 'cog',
      partName: 'edit',
    };
  }

  /** @internal */
  get leaveItem() {
    return {
      title: this.groupId
        ? this.t('actions.leave_group')
        : this.t('actions.leave_community'),
      action: this.actionFor('groupLeave'),
      role: 'destructive',
      icon: 'user-friends',
      partName: 'leave destructive',
    };
  }

  /** @internal */
  get adminItem() {
    return {
      title: this.t('actions.admin'),
      action: this.actionFor('groupVisit', [this.adminUrl]),
      icon: 'shield-check',
      partName: 'admin',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get items() {
    return [
      this.notificationsOn && this.notificationItem,
      this.favoriteOn && this.favoriteItem,
      this.tagsOn && this.tagsItem,
      this.reportOn && this.reportItem,
      this.editOn && this.settingsItem,

      (this.notificationsOn ||
        this.favoriteOn ||
        this.tagsOn ||
        this.reportOn ||
        this.editOn) &&
        (this.leaveOn || this.adminOn) &&
        this.divider,

      this.leaveOn && this.leaveItem,
      this.adminOn && this.adminItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.unitName,
      showCancel: true,
    };
  }

  activeKey(prop) {
    return this[prop] ? 'on' : 'off';
  }

  textBaseOnActiveKey(key, prop, opts) {
    return this.t(`${key}.${this.activeKey(prop)}`, opts);
  }

  actionFor(method, args) {
    return {
      menu: 'bu-group-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  toggleFavorite() {
    this.groupToggle({
      url: this.toggleFavoriteUrl,
      prop: 'favoriteValue',
      key: 'snack.favorite',
    });
  }

  toggleNotification() {
    this.groupToggle({
      url: this.toggleNotificationsUrl,
      prop: 'notificationsValue',
      key: 'snack.notifications',
    });
  }

  async groupToggle({ url, prop, key }) {
    try {
      await this.commonActions.post(url);
      document.querySelectorAll('bu-group-menu').forEach(item => {
        if (item.unitId == this.unitId) {
          item[prop] = !item[prop];
        }
      });
      this.commonActions.displaySnackBarSuccess(
        this.textBaseOnActiveKey(key, prop)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async groupLeave() {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }

    try {
      await this.commonActions.post(this.leaveUrl);
      this.groupVisit(this.communityOrGroupUrl);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  groupExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  groupVisit(url) {
    this.commonActions.visit(url);
  }

  render() {
    return html`
      <ef-dot-menu
        .config=${this.dotMenuConfig}
        .buttonClass=${this.buttonClass}>
      </ef-dot-menu>
    `;
  }
}

GroupMenu.registerTagName('bu-group-menu');
GroupMenu.registerCommonActions('bu-group-menu');

customElements.get('bu-group-menu') ||
  customElements.define('bu-group-menu', GroupMenu);
